import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Loader2, LogOut, Mail, ShoppingCart, Truck, Package, MapPin, AlertCircle, Leaf, CalendarDays, Clock, Check, Info, Plus, Trash2, Building2, CheckCircle, XCircle, MessageSquare, Phone, ShieldCheck, ArrowLeft, Camera, X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { nextWorkingDeliveryDay, firstStandardDeliveryDay, isExpressAllowed } from '../data/deliveryDates';

const AddressMap = lazy(() => import('./AddressMap'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOLUMES = [
  { value: '3m³', label: '3m³', desc: 'Terre et gravats uniquement' },
  { value: '8m³', label: '8m³', desc: 'Petit chantier' },
  { value: '10m³', label: '10m³', desc: 'Moyen chantier' },
  { value: '15m³', label: '15m³', desc: 'Grand chantier' },
  { value: '20m³', label: '20m³', desc: 'Très grand' },
  { value: '30m³', label: '30m³', desc: 'Industriel' },
];

const WASTE_TYPES = [
  { value: 'Gravat/terre', label: 'Gravat/terre', desc: 'Mélange gravats et terre' },
  { value: 'Terre', label: 'Terre', desc: 'Terre, remblais' },
  { value: 'Mélange (DIB)', label: 'Mélange (DIB)', desc: 'Bois, plâtre, plastique' },
  { value: 'Végétaux', label: 'Végétaux', desc: 'Déchets verts, branches' },
  { value: 'Bois', label: 'Bois', desc: 'Palettes, charpentes' },
  { value: 'Mobilier de bureau', label: 'Mobilier de bureau', desc: 'Bureaux, chaises, armoires' },
  { value: 'Cartons', label: 'Cartons', desc: 'Emballages, cartons' },
];

const PRICES = {
  "3m³": { "Terre": 245, "Gravat/terre": 245 },
  "8m³": { "Mélange (DIB)": 400, "Végétaux": 300, "Terre": 300, "Gravat/terre": 300, "Bois": 300, "Cartons": null, "Mobilier de bureau": null },
  "10m³": { "Mélange (DIB)": 480, "Végétaux": 350, "Terre": 350, "Gravat/terre": 350, "Bois": 350, "Cartons": null, "Mobilier de bureau": null },
  "15m³": { "Mélange (DIB)": 675, "Végétaux": 675, "Terre": null, "Gravat/terre": null, "Bois": 675, "Cartons": 675, "Mobilier de bureau": null },
  "20m³": { "Mélange (DIB)": 860, "Végétaux": 860, "Terre": null, "Gravat/terre": null, "Bois": 860, "Cartons": null, "Mobilier de bureau": null },
  "30m³": { "Mélange (DIB)": 1185, "Végétaux": 1185, "Terre": null, "Gravat/terre": null, "Bois": 1185, "Cartons": 1185, "Mobilier de bureau": 1185 },
};

const TIME_SLOTS = [
  { value: '8h-12h', label: '8h - 12h (Matin)' },
  { value: '12h-16h', label: '12h - 16h (Après-midi)' },
  { value: '8h-16h', label: '8h - 16h (Toute la journée)' },
  { value: 'custom', label: 'Autre créneau...' },
];

const PRESTATIONS = [
  {
    value: 'depot',
    label: 'Dépôt',
    desc: 'Nous installons une benne à l\'endroit souhaité (chantier, domicile ou terrain privé). Si elle doit être placée sur la voie publique, une autorisation peut être nécessaire. La benne peut rester sur place pendant une durée allant jusqu\'à deux semaines.',
  },
  {
    value: 'express',
    label: 'Livraison Express',
    desc: 'Livraison prioritaire au prochain jour de livraison (du lundi au vendredi), selon disponibilité. Supplément de 100€ HT. Indisponible pour une date ultérieure.',
  },
  {
    value: 'passante',
    label: 'Chargement Express',
    desc: 'Une benne est mise à votre disposition le temps du chargement. Le chauffeur reste sur place pendant l\'opération, généralement autour de 45 minutes. Cette solution rapide ne nécessite pas de démarche administrative.',
  },
  {
    value: 'echange',
    label: 'Rotation',
    desc: 'Lorsque votre benne est remplie, nous venons l\'enlever et déposons une nouvelle benne vide afin que vous puissiez continuer vos travaux.',
  },
  {
    value: 'retrait',
    label: 'Retrait',
    desc: 'Nous venons récupérer la benne déposée sur votre chantier.',
  },
];

const MonCompte = () => {
  const navigate = useNavigate();
  const location = window.location;
  const { user, logout, loading } = useAuth();
  const { toast } = useToast();

  // Guest mode: read guest data from localStorage
  const guestData = (() => {
    try { return JSON.parse(localStorage.getItem('ecoripeurs_guest') || 'null'); } catch { return null; }
  })();
  const isGuest = !user && !!guestData;

  // Check for prefilled data from navigation state
  const navState = typeof window !== 'undefined' && window.history.state?.usr;
  const prefillAddress = navState?.prefillAddress;
  const prefillVolume = navState?.volume;

  // Restore form data from localStorage
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem('ecoripeurs_form') || '{}'); } catch { return {}; }
  })();

  const [selectedVolume, setSelectedVolume] = useState(prefillVolume || saved.selectedVolume || '');
  const [selectedType, setSelectedType] = useState(saved.selectedType || '');
  const [selectedPrestation, setSelectedPrestation] = useState(prefillVolume ? 'depot' : saved.selectedPrestation || '');
  const [adresseChantier, setAdresseChantier] = useState(prefillAddress || saved.adresseChantier || '');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [addressValid, setAddressValid] = useState(null);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showHorsIdfPopup, setShowHorsIdfPopup] = useState(false);
  const [latitude, setLatitude] = useState(saved.latitude || null);
  const [longitude, setLongitude] = useState(saved.longitude || null);
  const [latInput, setLatInput] = useState(saved.latInput || '');
  const [lngInput, setLngInput] = useState(saved.lngInput || '');
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [acceptedConditions, setAcceptedConditions] = useState(saved.acceptedConditions || {});
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(saved.selectedDate ? new Date(saved.selectedDate) : undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(saved.selectedTimeSlot || '');
  const [customTimeSlot, setCustomTimeSlot] = useState(saved.customTimeSlot || '');
  const [expressSupplement, setExpressSupplement] = useState(saved.expressSupplement || false);
  // Rotation/Retrait check
  const [hasDepot, setHasDepot] = useState(null);
  const [hasDepotOrRotation, setHasDepotOrRotation] = useState(null);
  const [activeBenneTypes, setActiveBenneTypes] = useState([]);
  const [activeBenneVolumes, setActiveBenneVolumes] = useState([]);
  const [checkingDepot, setCheckingDepot] = useState(false);
  // Chantiers
  const [chantiers, setChantiers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [showAddChantier, setShowAddChantier] = useState(false);
  const [newChantierNom, setNewChantierNom] = useState('');
  const [newChantierAdresse, setNewChantierAdresse] = useState('');
  // Chantier extra info
  const [commentaire, setCommentaire] = useState(saved.commentaire || '');
  const [contactChantier, setContactChantier] = useState(saved.contactChantier || '');
  const [telContactChantier, setTelContactChantier] = useState(saved.telContactChantier || '');
  const [typeAcces, setTypeAcces] = useState(saved.typeAcces || 'privee');
  const [showVoiePubliquePopup, setShowVoiePubliquePopup] = useState(false);
  const [typeBenne, setTypeBenne] = useState(saved.typeBenne || 'indifferent');
  // Adresse de facturation (entreprise uniquement)
  const [adresseFactDifferente, setAdresseFactDifferente] = useState(saved.adresseFactDifferente || false);
  const [adresseFacturation, setAdresseFacturation] = useState(saved.adresseFacturation || '');
  const [cpFacturation, setCpFacturation] = useState(saved.cpFacturation || '');
  const [villeFacturation, setVilleFacturation] = useState(saved.villeFacturation || '');
  const [photos, setPhotos] = useState([]); // [{file, preview, uploading, filename}]
  const MAX_PHOTOS = 3;
  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount, type}
  const [promoError, setPromoError] = useState('');
  const [checkingPromo, setCheckingPromo] = useState(false);

  // Recalculate promo when price changes
  useEffect(() => {
    if (promoApplied && calculatedPrice) {
      const prix_num = parseFloat(String(calculatedPrice).replace('€', '').replace('HT', '').replace(' ', '').replace(',', '.')) || 0;
      let discounted;
      if (promoApplied.discount_type === 'percent') {
        const discount_amount = Math.round(prix_num * promoApplied.discount_value / 100 * 100) / 100;
        discounted = Math.round((prix_num - discount_amount) * 100) / 100;
      } else {
        discounted = Math.max(0, Math.round((prix_num - promoApplied.discount_value) * 100) / 100);
      }
      setPromoApplied(prev => ({ ...prev, original_price: prix_num, discounted_price: discounted }));
    }
  }, [calculatedPrice]);
  useEffect(() => {
    const formData = {
      selectedVolume, selectedType, selectedPrestation, adresseChantier,
      latitude, longitude, latInput, lngInput, acceptedConditions,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      selectedTimeSlot, customTimeSlot, expressSupplement,
      commentaire, contactChantier, telContactChantier,
      typeAcces, typeBenne,
      adresseFactDifferente, adresseFacturation, cpFacturation, villeFacturation
    };
    localStorage.setItem('ecoripeurs_form', JSON.stringify(formData));
  }, [selectedVolume, selectedType, selectedPrestation, adresseChantier,
      latitude, longitude, latInput, lngInput, acceptedConditions,
      selectedDate, selectedTimeSlot, customTimeSlot, expressSupplement,
      commentaire, contactChantier, telContactChantier, typeAcces, typeBenne,
      adresseFactDifferente, adresseFacturation, cpFacturation, villeFacturation]);

  const isValidPhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/[\s.\-]/g, '');
    return /^(0[1-9]\d{8}|\+33[1-9]\d{8}|0033[1-9]\d{8})$/.test(cleaned);
  };
  const now = new Date();
  const tomorrow = nextWorkingDeliveryDay(now);
  const isPast14h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', hour: '2-digit', hourCycle: 'h23' }).format(now)) >= 16;
  const expressAvailableForDate = isExpressAllowed(selectedDate, now);
  const hasExpressAccess = expressAvailableForDate && (selectedPrestation === 'express' || expressSupplement);
  const minDate = hasExpressAccess ? tomorrow : firstStandardDeliveryDay(now);

  useEffect(() => {
    if (selectedDate && !isExpressAllowed(selectedDate)) {
      setExpressSupplement(false);
      setSelectedPrestation(previous => previous === 'express' ? 'depot' : previous);
    }
  }, [selectedDate]);

  const disabledDays = [
    { before: minDate },
    { dayOfWeek: [0, 6] },
  ];

  useEffect(() => {
    if (!loading && !user && !isGuest) {
      navigate('/connexion');
    }
  }, [user, loading, navigate, isGuest]);

  // Address verification + suggestions via api-adresse.data.gouv.fr
  useEffect(() => {
    if (reverseGeocoding) return;
    let cancelled = false;
    setAddressValid(null);
    setAddressSuggestions([]);
    setAddressError('');
    setLatitude(null);
    setLongitude(null);
    if (adresseChantier.trim().length < 4) {
      setAddressValid(null);
      setAddressSuggestions([]);
      return;
    }
    setCheckingAddress(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(adresseChantier.trim())}&limit=5`, { timeout: 8000 });
        if (cancelled) return;
        const features = res.data?.features || [];
        
        // Show suggestions for autocomplete
        setAddressSuggestions(features);
        
        if (features.length > 0 && adresseChantier.trim().length >= 10) {
          const input = adresseChantier.trim().toLowerCase();
          const inputHasNumber = /^\s*\d/.test(input);
          const inputHasPostcode = /\b\d{5}\b/.test(input);
          
          let valid = false;
          let bestFeature = null;
          
          for (const f of features) {
            const p = f.properties;
            if (inputHasNumber && p.type !== 'housenumber') continue;
            if (p.score < 0.6) continue;
            
            // L'adresse saisie doit contenir le code postal OU le nom de la ville
            const cityMatch = p.city && input.includes(p.city.toLowerCase());
            const postcodeMatch = p.postcode && input.includes(p.postcode);
            
            if (inputHasPostcode ? postcodeMatch : cityMatch) {
              valid = true;
              bestFeature = f;
              break;
            }
          }
          
          // Si pas de ville/CP trouvé dans la saisie, invalide
          if (!valid && !inputHasPostcode && !inputHasNumber && features[0].properties.score > 0.85) {
            // Score très élevé = adresse presque exacte, on accepte
            valid = true;
            bestFeature = features[0];
          }
          
          setAddressValid(valid);
          if (!valid) {
            setAddressError('Précisez la ville ou le code postal');
            setShowHorsIdfPopup(false);
          } else {
            setAddressError('');
            setAddressSuggestions([]);
            // Vérification Île-de-France via le code postal
            const postcode = bestFeature?.properties?.postcode || '';
            const idfPrefixes = ['75', '77', '78', '91', '92', '93', '94', '95'];
            const isIdf = idfPrefixes.some(p => postcode.startsWith(p));
            setShowHorsIdfPopup(!isIdf);
          }
          if (valid && bestFeature?.geometry) {
            const [lon, lat] = bestFeature.geometry.coordinates;
            setLatitude(lat);
            setLongitude(lon);
            setLatInput(lat.toFixed(6));
            setLngInput(lon.toFixed(6));
          }
        } else if (adresseChantier.trim().length >= 10) {
          setAddressValid(false);
          setAddressError('Adresse introuvable : vérifiez le numéro, la rue et le code postal.');
        }
      } catch {
        if (cancelled) return;
        setAddressValid(null);
        setAddressError('La vérification est indisponible. Réessayez ou indiquez la position du chantier sur la carte.');
      } finally {
        if (!cancelled) setCheckingAddress(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [adresseChantier, reverseGeocoding]);

  // Select an address from suggestions dropdown
  const selectAddressSuggestion = (feature) => {
    const label = feature.properties.label;
    const postcode = feature.properties.postcode || '';
    setAdresseChantier(label);
    setAddressSuggestions([]);
    setAddressError('');
    setSelectedChantier(null);
    
    // Extract coordinates
    if (feature.geometry) {
      const [lon, lat] = feature.geometry.coordinates;
      setLatitude(lat);
      setLongitude(lon);
      setLatInput(lat.toFixed(6));
      setLngInput(lon.toFixed(6));
    }
    
    // Check IDF
    const idfPrefixes = ['75', '77', '78', '91', '92', '93', '94', '95'];
    const isIdf = idfPrefixes.some(p => postcode.startsWith(p));
    setAddressValid(true);
    setShowHorsIdfPopup(!isIdf);
  };

  // Reverse geocode from coordinates
  const reverseGeocode = async (lat, lng) => {
    setReverseGeocoding(true);
    setLatitude(lat);
    setLongitude(lng);
    setLatInput(lat.toFixed(6));
    setLngInput(lng.toFixed(6));
    try {
      const res = await axios.get(`https://data.geopf.fr/geocodage/reverse/?lon=${lng}&lat=${lat}`);
      const features = res.data?.features || [];
      if (features.length > 0) {
        const addr = features[0].properties.label;
        setAdresseChantier(addr);
        setAddressValid(true);
        setAddressError('');
        setSelectedChantier(null);
      } else {
        setAddressValid(null);
        setAddressError('Aucune adresse trouvée à cet emplacement');
      }
    } catch {
      setAddressError('Erreur lors de la géolocalisation inverse');
    } finally {
      setTimeout(() => setReverseGeocoding(false), 800);
    }
  };

  const handleMapClick = (lat, lng) => {
    reverseGeocode(lat, lng);
  };

  const handleCoordsSubmit = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      reverseGeocode(lat, lng);
    }
  };

  const checkDepotExists = async (adresse) => {
    if (!user) {
      setHasDepot(false); setHasDepotOrRotation(false);
      setActiveBenneTypes([]); setActiveBenneVolumes([]);
      return;
    }
    const emailToCheck = user?.email || guestData?.email;
    if (!emailToCheck) return;
    setCheckingDepot(true);
    try {
      const params = adresse ? `?adresse=${encodeURIComponent(adresse)}` : '';
      const res = await axios.get(`${API}/devis/check-depot/${emailToCheck}${params}`);
      setHasDepot(res.data.has_depot);
      setHasDepotOrRotation(res.data.has_depot_or_rotation);
      setActiveBenneTypes(res.data.active_benne_types || []);
      setActiveBenneVolumes(res.data.active_benne_volumes || []);
      if (!res.data.has_depot && selectedPrestation === 'echange') {
        setSelectedPrestation('');
      }
      if (!res.data.has_depot_or_rotation && selectedPrestation === 'retrait') {
        setSelectedPrestation('');
      }
    } catch {
      setHasDepot(false);
      setHasDepotOrRotation(false);
      setActiveBenneTypes([]);
      setActiveBenneVolumes([]);
    } finally {
      setCheckingDepot(false);
    }
  };

  useEffect(() => {
    if (user) checkDepotExists(adresseChantier);
  }, [user, adresseChantier]);

  const fetchChantiers = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/chantiers/${user.id}`);
      setChantiers(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (user) fetchChantiers();
  }, [user]);

  const handleAddChantier = async () => {
    if (!newChantierNom.trim() || !newChantierAdresse.trim()) {
      toast({ title: "Champs requis", description: "Remplissez le nom et l'adresse", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.post(`${API}/chantiers/${user.id}`, {
        nom: newChantierNom.trim(),
        adresse: newChantierAdresse.trim(),
      });
      setChantiers(prev => [res.data, ...prev]);
      selectChantier(res.data);
      setShowAddChantier(false);
      setNewChantierNom('');
      setNewChantierAdresse('');
      toast({ title: "Chantier ajouté", description: `"${res.data.nom}" enregistré` });
    } catch {
      toast({ title: "Erreur", description: "Impossible d'ajouter le chantier", variant: "destructive" });
    }
  };

  const handleDeleteChantier = async (id) => {
    try {
      await axios.delete(`${API}/chantiers/${id}`);
      setChantiers(prev => prev.filter(c => c.id !== id));
      if (selectedChantier?.id === id) {
        setSelectedChantier(null);
        setAdresseChantier('');
      }
      toast({ title: "Chantier supprimé" });
    } catch {
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  const selectChantier = (ch) => {
    if (selectedChantier?.id === ch.id) {
      setSelectedChantier(null);
      setAdresseChantier('');
      return;
    }
    setSelectedChantier(ch);
    setAdresseChantier(ch.adresse);
    setAddressError('');
  };

  const handlePrestationSelect = (value) => {
    if (value === 'express' && !expressAvailableForDate) return;
    if (value === 'echange' && hasDepot === false) {
      const chantierMsg = adresseChantier
        ? `Aucun dépôt enregistré pour ce chantier (${adresseChantier}). Commandez d'abord un "Dépôt" sur cette adresse.`
        : 'Vous devez d\'abord sélectionner un chantier et y avoir commandé un dépôt de benne.';
      toast({ title: "Rotation impossible", description: chantierMsg, variant: "destructive" });
      return;
    }
    if (value === 'retrait' && hasDepotOrRotation === false) {
      const chantierMsg = adresseChantier
        ? `Aucun dépôt ni rotation enregistré pour ce chantier (${adresseChantier}). Un dépôt ou une rotation doit avoir été effectué au préalable.`
        : 'Vous devez d\'abord sélectionner un chantier ayant eu un dépôt ou une rotation.';
      toast({ title: "Retrait impossible", description: chantierMsg, variant: "destructive" });
      return;
    }
    setSelectedPrestation(prev => prev === value ? '' : value);

    // If switching away from express after 14h, and tomorrow is selected, reset date only if no supplement
    if (isPast14h && value !== 'express' && !expressSupplement && selectedDate) {
      const tomorrowCheck = nextWorkingDeliveryDay();
      if (selectedDate.toDateString() === tomorrowCheck.toDateString()) {
        setSelectedDate(undefined);
        toast({ title: "Date réinitialisée", description: "Après 16h, la livraison au prochain jour de livraison nécessite le supplément Express (+100€ HT).", variant: "destructive" });
      }
    }

    // Reset type if locked out by new prestation (retrait only)
    if (value === 'retrait' && activeBenneTypes.length > 0 && selectedType && !activeBenneTypes.includes(selectedType)) {
      setSelectedType('');
    }
    // Reset volume if locked out by new prestation (retrait only)
    if (value === 'retrait' && activeBenneVolumes.length > 0 && selectedVolume && !activeBenneVolumes.includes(selectedVolume)) {
      setSelectedVolume('');
    }
  };

  useEffect(() => {
    if (selectedVolume && selectedType) {
      const vol = PRICES[selectedVolume];
      if (vol && vol[selectedType]) {
        const base = vol[selectedType];
        // Tarif HT identique pour professionnels et particuliers.
        const adjustedBase = base;
        const supplement = (selectedPrestation === 'express' || expressSupplement) ? 100 : 0;
        setCalculatedPrice(adjustedBase + supplement);
      } else {
        setCalculatedPrice(null);
      }
    } else {
      setCalculatedPrice(null);
    }
  }, [selectedVolume, selectedType, selectedPrestation, expressSupplement]);

  const validateAddress = () => {
    let valid = true;
    if (!adresseChantier || adresseChantier.trim().length < 10) {
      setAddressError("L'adresse doit contenir au moins 10 caractères (ex: 12 rue des Lilas, 75001 Paris)");
      valid = false;
    } else {
      const hasNumbers = /\d/.test(adresseChantier);
      const hasLetters = /[a-zA-Z]/.test(adresseChantier);
      if (!hasNumbers || !hasLetters) {
        setAddressError("L'adresse doit contenir un numéro et un nom de rue");
        valid = false;
      } else {
        setAddressError('');
      }
    }
    return valid;
  };

  const getTimeSlotValue = () => {
    if (selectedTimeSlot === 'custom') return customTimeSlot.trim();
    return selectedTimeSlot;
  };

  const isDateValid = !!selectedDate && (selectedPrestation !== 'express' || expressAvailableForDate);
  const isTimeValid = selectedTimeSlot && (selectedTimeSlot !== 'custom' || customTimeSlot.trim().length > 0);
  const allConditionsAccepted = ['cond1', 'cond2', 'cond3', 'cond4', 'cond5', 'cond6', 'cgu'].every(k => acceptedConditions[k]);
  const hasValidLocation = addressValid === true || (showMap && latitude && longitude);
  const isDevisFormComplete = selectedPrestation && selectedVolume && selectedType && calculatedPrice && hasValidLocation && isDateValid && isTimeValid;
  const isFormComplete = isDevisFormComplete && allConditionsAccepted;

  const buildPayload = () => {
    const useGpsOnly = showMap && latitude && longitude && !addressValid;
    const source = isGuest ? guestData : user;
    return {
      type: source?.type || 'particulier',
      name: source?.name || '',
      email: source?.email || '',
      phone: source?.phone || '',
      postalCode: (isGuest ? guestData?.postalCode : '') || '',
      adresseChantier: useGpsOnly ? `GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : adresseChantier.trim(),
      serviceType: 'benne',
      prestation: selectedPrestation,
      benneVolume: selectedVolume,
      benneType: selectedType,
      dateDebut: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
      dateFin: '',
      creneauHoraire: getTimeSlotValue(),
      commentaire: commentaire.trim(),
      contactChantier: contactChantier.trim(),
      telContactChantier: telContactChantier.trim(),
      typeAcces: typeAcces,
      typeBenne: typeBenne,
      latitude: (showMap && latitude) ? latitude : null,
      longitude: (showMap && longitude) ? longitude : null,
      photos: photos.filter(p => p.filename).map(p => p.filename),
      promoCode: promoApplied ? promoApplied.code : null,
      promoDiscount: promoApplied ? promoApplied.discount : null,
      expressSupplement: expressSupplement,
      adresseFacturation: adresseFactDifferente ? adresseFacturation.trim() : '',
      cpFacturation: adresseFactDifferente ? cpFacturation.trim() : '',
      villeFacturation: adresseFactDifferente ? villeFacturation.trim() : '',
      societe: isGuest ? (guestData?.societe || '') : undefined,
    };
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setCheckingPromo(true);
    setPromoError('');
    try {
      const res = await axios.post(`${API}/promo/validate`, { code: promoCode.trim(), prix: calculatedPrice });
      setPromoApplied(res.data);
      setPromoError('');
    } catch (err) {
      setPromoApplied(null);
      setPromoError(err.response?.data?.detail || 'Code invalide');
    }
    setCheckingPromo(false);
  };

  const handleEnvoyerDevis = async () => {
    if (!selectedVolume || !selectedType) {
      toast({ title: "Sélection incomplète", description: "Veuillez choisir un volume et un type de déchet", variant: "destructive" });
      return;
    }
    if (!validateAddress() && !(showMap && latitude && longitude)) {
      toast({ title: "Adresse invalide", description: "Veuillez corriger l'adresse ou activer la localisation GPS", variant: "destructive" });
      return;
    }
    if (!isDateValid || !isTimeValid) {
      toast({ title: "Planning incomplet", description: "Veuillez choisir une date et un créneau horaire", variant: "destructive" });
      return;
    }
    if (telContactChantier && !isValidPhone(telContactChantier)) {
      toast({ title: "Téléphone invalide", description: "Format attendu : 10 chiffres (06...), +33... ou 0033...", variant: "destructive" });
      return;
    }
    if (!telContactChantier) {
      toast({ title: "Téléphone obligatoire", description: "Veuillez renseigner le numéro de téléphone du contact chantier", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/devis/send-email-only`, buildPayload());
      toast({ title: "Devis envoyé !", description: "Vous recevrez le devis par email sous quelques minutes" });
    } catch (error) {
      toast({ title: "Erreur", description: "Une erreur est survenue lors de l'envoi", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommander = async () => {
    if (!selectedVolume || !selectedType) {
      toast({ title: "Sélection incomplète", description: "Veuillez choisir un volume et un type de déchet", variant: "destructive" });
      return;
    }
    if (!validateAddress() && !(showMap && latitude && longitude)) {
      toast({ title: "Adresse invalide", description: "Veuillez corriger l'adresse ou activer la localisation GPS", variant: "destructive" });
      return;
    }
    if (!isDateValid || !isTimeValid) {
      toast({ title: "Planning incomplet", description: "Veuillez choisir une date et un créneau horaire", variant: "destructive" });
      return;
    }
    if (telContactChantier && !isValidPhone(telContactChantier)) {
      toast({ title: "Téléphone invalide", description: "Format attendu : 10 chiffres (06...), +33... ou 0033...", variant: "destructive" });
      return;
    }
    if (!telContactChantier) {
      toast({ title: "Téléphone obligatoire", description: "Veuillez renseigner le numéro de téléphone du contact chantier", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/devis`, buildPayload());
      navigate(`/paiement/${response.data.id}`);
    } catch (error) {
      toast({ title: "Erreur", description: "Une erreur est survenue", variant: "destructive" });
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <Loader2 className="h-12 w-12 animate-spin text-green-600" />
      </div>
    );
  }

  if (!user && !isGuest) return null;

  const isGravatsDisabled = (vol) => ['15m³', '20m³', '30m³'].includes(vol);
  const isTerreDisabled = (vol) => ['15m³', '20m³', '30m³'].includes(vol);

  const isTypeDisabledForVolume = (type, vol) => {
    if (!vol) return false;
    const price = PRICES[vol]?.[type];
    return price === null || price === undefined;
  };

  const getMissingFieldMessage = () => {
    if (!selectedPrestation) return "Choisissez un type de prestation";
    if (!selectedVolume) return "Sélectionnez un volume";
    if (!selectedType) return "Sélectionnez un type de déchet";
    if (!adresseChantier || adresseChantier.trim().length < 10) return "Renseignez l'adresse du chantier";
    if (addressValid === false && !(showMap && latitude && longitude)) return "L'adresse est introuvable, corrigez-la ou activez le GPS";
    if (addressValid === null && !checkingAddress && !(showMap && latitude && longitude)) return "Renseignez une adresse valide";
    if (!isDateValid) return "Sélectionnez une date de livraison";
    if (!isTimeValid) return "Choisissez un créneau horaire";
    if (!allConditionsAccepted) return "Cochez les conditions d'évacuation, CGV & CGU";
    return "Complétez tous les champs";
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return null;
    return format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="mon-compte-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900" data-testid="welcome-heading">
              Bonjour, {user?.name || guestData?.name || 'Invité'}
            </h1>
            <p className="text-gray-500 text-sm">
              Choisissez votre benne et obtenez le prix instantanément
            </p>
          </div>
          {user ? (
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-green-300 text-green-700 hover:bg-green-50" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Mes commandes
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/')} className="border-green-300 text-green-700 hover:bg-green-50" data-testid="back-to-home">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Prestation type */}
            <Card className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Quelle prestation ?
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="prestation-selector">
                {PRESTATIONS.map(({ value, label, desc }) => {
                  const isRotationBlocked = value === 'echange' && hasDepot === false;
                  const isRetraitBlocked = value === 'retrait' && hasDepotOrRotation === false;
                  const isExpressBlocked = value === 'express' && !expressAvailableForDate;
                  const isBlocked = isRotationBlocked || isRetraitBlocked || isExpressBlocked;
                  const blockedMsg = isExpressBlocked
                    ? 'Réservée au prochain jour de livraison'
                    : isRotationBlocked
                    ? 'Aucun dépôt sur ce chantier'
                    : isRetraitBlocked
                    ? 'Aucun dépôt/rotation sur ce chantier'
                    : '';
                  return (
                    <div key={value} className="relative group">
                      <button
                        onClick={() => handlePrestationSelect(value)}
                        disabled={isBlocked || checkingDepot}
                        data-testid={`prestation-${value}`}
                        className={`w-full p-4 border-2 rounded-xl text-center transition-all ${
                          selectedPrestation === value
                            ? 'border-green-400 bg-green-50'
                            : isBlocked
                            ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                            : 'border-gray-200 hover:border-green-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          {selectedPrestation === value && (
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          )}
                          <span className={`font-bold text-sm ${isBlocked ? 'text-gray-400' : 'text-gray-900'}`}>{label}</span>
                          <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        </div>
                        {isBlocked && (
                          <div className="text-xs text-red-500 mt-1">{blockedMsg}</div>
                        )}
                      </button>
                      <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-600 leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                        {desc}
                        {isBlocked && (
                          <p className="mt-2 text-red-500 font-medium">
                            {isExpressBlocked
                              ? 'Choisissez un dépôt standard pour une date après le prochain jour de livraison.'
                              : isRotationBlocked
                              ? 'Vous devez d\'abord commander un dépôt de benne sur ce chantier avant de pouvoir demander une rotation.'
                              : 'Un dépôt ou une rotation doit avoir été effectué sur ce chantier avant de pouvoir demander un retrait.'}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Volume */}
            <Card className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-green-600" />
                Volume de la benne
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="volume-selector">
                {VOLUMES.map(({ value, label, desc }) => {
                  const isVolumeLocked = (selectedPrestation === 'retrait')
                    && activeBenneVolumes.length > 0
                    && !activeBenneVolumes.includes(value);
                  const isWasteIncompatible = Boolean(selectedType) && isTypeDisabledForVolume(selectedType, value);
                  const isVolumeDisabled = isVolumeLocked || isWasteIncompatible;
                  return (
                    <button
                      key={value}
                      onClick={() => {
                        if (isVolumeDisabled) return;
                        if (selectedVolume === value) { setSelectedVolume(''); return; }
                        setSelectedVolume(value);
                        if (selectedType && isTypeDisabledForVolume(selectedType, value)) setSelectedType('');
                        if (value === '30m³') setTypeBenne('ampiroll');
                      }}
                      disabled={isVolumeDisabled}
                      data-testid={`volume-${value}`}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        isVolumeDisabled
                          ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                          : selectedVolume === value
                          ? 'border-green-600 bg-green-50 ring-2 ring-green-200'
                          : 'border-gray-200 hover:border-green-300 bg-white'
                      }`}
                    >
                      <div className={`font-bold text-xl ${isVolumeDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{label}</div>
                      <div className="text-xs text-gray-500 mt-1">{desc}</div>
                      {isWasteIncompatible && <div className="text-xs text-red-500 mt-1">{value === '3m³' ? 'Terre / gravats uniquement' : 'Indisponible pour ces déchets'}</div>}
                      {isVolumeLocked && <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">Pas sur ce chantier</Badge>}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Waste types */}
            <Card className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-green-600" />
                Type de déchets
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="waste-type-selector">
                {WASTE_TYPES.map(({ value, label, desc }) => {
                  const isUnavailableForVolume = isTypeDisabledForVolume(value, selectedVolume);
                  const isTypeLockedOut = (selectedPrestation === 'retrait')
                    && activeBenneTypes.length > 0
                    && !activeBenneTypes.includes(value);
                  const disabled = isUnavailableForVolume || isTypeLockedOut;
                  return (
                    <button
                      key={value}
                      onClick={() => !disabled && setSelectedType(prev => prev === value ? '' : value)}
                      disabled={disabled}
                      data-testid={`type-${value}`}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        selectedType === value
                          ? 'border-green-600 bg-green-50 ring-2 ring-green-200'
                          : disabled
                          ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-green-300 bg-white'
                      }`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{label}</div>
                      <div className="text-xs text-gray-500 mt-1">{desc}</div>
                      {isUnavailableForVolume && <Badge className="mt-2 bg-red-100 text-red-700 text-xs">Indisponible</Badge>}
                      {isTypeLockedOut && <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">Pas sur ce chantier</Badge>}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Address / Chantiers */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  Adresse du chantier
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddChantier(!showAddChantier)}
                  data-testid="add-chantier-btn"
                  className="text-green-600 border-green-600 hover:bg-green-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un chantier
                </Button>
              </div>

              {/* Saved chantiers */}
              {chantiers.length > 0 && (
                <div className="mb-4 space-y-2" data-testid="chantiers-list">
                  {chantiers.map((ch) => (
                    <div
                      key={ch.id}
                      className={`flex items-center justify-between p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedChantier?.id === ch.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 bg-white'
                      }`}
                      onClick={() => selectChantier(ch)}
                      data-testid={`chantier-${ch.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Building2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-gray-900 truncate">{ch.nom}</div>
                          <div className="text-xs text-gray-500 truncate">{ch.adresse}</div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteChantier(ch.id); }}
                        className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2"
                        data-testid={`delete-chantier-${ch.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add chantier form */}
              {showAddChantier && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3" data-testid="add-chantier-form">
                  <div>
                    <Label className="text-sm font-medium">Nom du chantier *</Label>
                    <Input
                      data-testid="new-chantier-nom"
                      className="mt-1 h-10"
                      value={newChantierNom}
                      onChange={(e) => setNewChantierNom(e.target.value)}
                      placeholder="Ex: Maison Montreuil, Bureau Paris 11..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Adresse *</Label>
                    <Input
                      data-testid="new-chantier-adresse"
                      className="mt-1 h-10"
                      value={newChantierAdresse}
                      onChange={(e) => setNewChantierAdresse(e.target.value)}
                      placeholder="12 rue des Lilas, 75001 Paris"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddChantier}
                      size="sm"
                      data-testid="save-chantier-btn"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Enregistrer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddChantier(false)}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              )}

              {/* Manual address fields (pre-filled when chantier selected) */}
              {!selectedChantier && !showAddChantier && chantiers.length === 0 && (
                <p className="text-sm text-gray-500 mb-3">Aucun chantier enregistré. Ajoutez-en un pour le réutiliser facilement.</p>
              )}

              <div>
                <div>
                  <Label htmlFor="adresse" className="text-sm font-medium">Adresse complète *</Label>
                  <div className="relative">
                    <Input
                      id="adresse"
                      data-testid="adresse-chantier-input"
                      className={`mt-1 h-11 pr-10 ${addressError ? 'border-red-400' : addressValid === true ? 'border-green-400' : addressValid === false ? 'border-red-400' : ''}`}
                      value={adresseChantier}
                      onChange={(e) => { setAdresseChantier(e.target.value); setAddressError(''); setSelectedChantier(null); }}
                      placeholder="Commencez à taper votre adresse..."
                      autoComplete="off"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5" data-testid="address-status-icon">
                      {checkingAddress && <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />}
                      {!checkingAddress && addressValid === true && <CheckCircle className="h-5 w-5 text-green-500" />}
                      {!checkingAddress && addressValid === false && <XCircle className="h-5 w-5 text-red-500" />}
                    </div>
                    {addressSuggestions.length > 0 && !addressValid && (
                      <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto" data-testid="address-suggestions-dropdown">
                        {addressSuggestions.map((feat, i) => (
                          <button
                            key={i}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 border-b last:border-0"
                            data-testid={`address-suggestion-${i}`}
                            onClick={() => selectAddressSuggestion(feat)}
                          >
                            {feat.properties.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {addressError && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="address-error">
                      <AlertCircle className="h-3 w-3" />{addressError}
                    </p>
                  )}
                  {!addressError && addressValid === false && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1" data-testid="address-not-found">
                      <XCircle className="h-3 w-3" />Adresse introuvable
                    </p>
                  )}
                  {addressValid === true && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1" data-testid="address-found">
                      <CheckCircle className="h-3 w-3" />Adresse vérifiée
                    </p>
                  )}
                  {showHorsIdfPopup && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2" data-testid="hors-idf-popup">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-red-700">Zone non desservie</p>
                        <p className="text-xs text-red-600 mt-0.5">Nous livrons uniquement en Île-de-France (75, 77, 78, 91, 92, 93, 94, 95).</p>
                      </div>
                      <button onClick={() => setShowHorsIdfPopup(false)} className="ml-auto text-red-400 hover:text-red-600 flex-shrink-0">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle GPS / Map */}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Localisation GPS</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMap(!showMap)}
                  data-testid="toggle-map-btn"
                  className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${showMap ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${showMap ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Map + Coordinates (conditionally shown) */}
              {showMap && (
              <div className="mt-4 space-y-3">
                <Suspense fallback={<div className="h-[250px] bg-gray-100 rounded-lg flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}>
                  <AddressMap lat={latitude} lng={longitude} onMapClick={handleMapClick} />
                </Suspense>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Latitude</Label>
                    <Input
                      data-testid="latitude-input"
                      className="mt-1 h-9 text-sm"
                      value={latInput}
                      onChange={(e) => setLatInput(e.target.value)}
                      placeholder="48.8566"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Longitude</Label>
                    <Input
                      data-testid="longitude-input"
                      className="mt-1 h-9 text-sm"
                      value={lngInput}
                      onChange={(e) => setLngInput(e.target.value)}
                      placeholder="2.3522"
                    />
                  </div>
                  <Button
                    onClick={handleCoordsSubmit}
                    size="sm"
                    variant="outline"
                    className="h-9 px-3 text-xs"
                    data-testid="coords-submit-btn"
                    disabled={reverseGeocoding}
                  >
                    {reverseGeocoding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Localiser'}
                  </Button>
                </div>
                {latitude && longitude && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Coordonnées : {latitude.toFixed(6)}, {longitude.toFixed(6)}
                  </p>
                )}
              </div>
              )}

              {/* Access type */}
              <div className="mt-4">
                <Label className="text-sm font-medium mb-2 block">Type d'accès</Label>
                <div className="flex gap-3" data-testid="type-acces-selector">
                  {[
                    { value: 'publique', label: 'Voie publique' },
                    { value: 'privee', label: 'Voie privée' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        const newValue = typeAcces === value ? '' : value;
                        setTypeAcces(newValue);
                        if (newValue === 'publique') setShowVoiePubliquePopup(true);
                      }}
                      data-testid={`acces-${value}`}
                      className={`flex-1 p-3 border-2 rounded-lg text-sm font-medium text-center transition-all ${
                        typeAcces === value
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-green-300 bg-white text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Popup voie publique */}
              {showVoiePubliquePopup && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowVoiePubliquePopup(false)}>
                  <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <AlertCircle className="h-6 w-6 text-amber-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Information importante</h3>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      En cas de dépôt de benne sur la voie publique (trottoir, stationnement ou chaussée), une autorisation d'occupation du domaine public délivrée par la mairie peut être requise. Il appartient au client d'effectuer les démarches nécessaires auprès de la commune concernée avant la livraison de la benne.
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed mt-3">
                      Si vous n'avez pas encore effectué la demande, voici le lien pour le formulaire CERFA :{' '}
                      <a href="https://entreprendre.service-public.fr/vosdroits/R17000" target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold underline hover:text-green-700">
                        Télécharger le CERFA 14023*01
                      </a>
                    </p>
                    <div className="mt-5 flex justify-end">
                      <button
                        onClick={() => setShowVoiePubliquePopup(false)}
                        className="px-5 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                        data-testid="popup-voie-publique-ok"
                      >
                        J'ai compris
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Type de benne */}
              <div className="mt-4">
                <Label className="text-sm font-medium mb-2 block">Type de benne</Label>
                <div className="flex gap-3" data-testid="type-benne-selector">
                  {[
                    { value: 'chaine', label: 'À chaîne' },
                    { value: 'ampiroll', label: 'Ampiroll' },
                    { value: 'indifferent', label: 'Sans préférence' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setTypeBenne(prev => prev === value ? '' : value)}
                      data-testid={`benne-type-${value}`}
                      className={`flex-1 p-3 border-2 rounded-lg text-sm font-medium text-center transition-all ${
                        typeBenne === value
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-green-300 bg-white text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact on site */}
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Contact sur le chantier</Label>
                  <Input
                    data-testid="contact-chantier-input"
                    className="mt-1 h-10"
                    value={contactChantier}
                    onChange={(e) => setContactChantier(e.target.value)}
                    placeholder="Nom et prénom"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Téléphone du contact *</Label>
                  <Input
                    data-testid="tel-contact-chantier-input"
                    className={`mt-1 h-10 ${telContactChantier && !isValidPhone(telContactChantier) ? 'border-red-400' : telContactChantier && isValidPhone(telContactChantier) ? 'border-green-400' : ''}`}
                    value={telContactChantier}
                    onChange={(e) => setTelContactChantier(e.target.value)}
                    placeholder="Numéro de téléphone"
                  />
                  {telContactChantier && !isValidPhone(telContactChantier) && (
                    <p className="text-xs text-red-500 mt-1">Format : 10 chiffres (06...), +33... ou 0033...</p>
                  )}
                </div>
              </div>

              {/* Comment */}
              <div className="mt-4">
                <Label className="text-sm font-medium">Commentaire</Label>
                <textarea
                  data-testid="commentaire-input"
                  className="mt-1 w-full min-h-[70px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-y"
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  placeholder="Précisions sur l'accès, contraintes, horaires particuliers..."
                />
              </div>

              {/* Adresse de facturation (entreprise uniquement) */}
              {(user?.type === 'professionnel' || guestData?.type === 'professionnel') && (
                <div className="mt-4" data-testid="billing-address-section">
                  <label className="flex items-center gap-2.5 cursor-pointer" data-testid="billing-address-checkbox-label">
                    <input
                      type="checkbox"
                      checked={adresseFactDifferente}
                      onChange={(e) => {
                        setAdresseFactDifferente(e.target.checked);
                        if (!e.target.checked) {
                          setAdresseFacturation('');
                          setCpFacturation('');
                          setVilleFacturation('');
                        }
                      }}
                      className="h-4 w-4 rounded border-2 border-green-500 text-green-600 focus:ring-green-400 accent-green-600"
                      data-testid="billing-address-checkbox"
                    />
                    <span className="text-sm font-medium text-gray-700">Adresse de facturation différente</span>
                  </label>
                  {adresseFactDifferente && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="billing-address-fields">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Adresse de facturation *</Label>
                        <Input
                          data-testid="adresse-facturation-input"
                          className="mt-1 h-10"
                          value={adresseFacturation}
                          onChange={(e) => setAdresseFacturation(e.target.value)}
                          placeholder="Numéro et rue"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Code postal *</Label>
                          <Input
                            data-testid="cp-facturation-input"
                            className="mt-1 h-10"
                            value={cpFacturation}
                            onChange={(e) => setCpFacturation(e.target.value)}
                            placeholder="75001"
                            maxLength={5}
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Ville *</Label>
                          <Input
                            data-testid="ville-facturation-input"
                            className="mt-1 h-10"
                            value={villeFacturation}
                            onChange={(e) => setVilleFacturation(e.target.value)}
                            placeholder="Paris"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Photos chantier */}
              <div className="mt-4" data-testid="photos-section">
                <Label className="text-sm font-medium mb-2 block">Photos du chantier ({photos.length}/{MAX_PHOTOS})</Label>
                <div className="flex flex-wrap gap-3">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                      <img src={photo.preview} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      {photo.uploading ? (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-white" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            URL.revokeObjectURL(photo.preview);
                            setPhotos(prev => prev.filter((_, i) => i !== idx));
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-photo-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <label
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 hover:border-green-500 flex flex-col items-center justify-center cursor-pointer transition-colors bg-gray-50 hover:bg-green-50"
                      data-testid="add-photo-btn"
                    >
                      <Camera className="h-6 w-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Ajouter</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast({ title: "Fichier trop volumineux", description: "Maximum 5 Mo par photo", variant: "destructive" });
                            return;
                          }
                          const preview = URL.createObjectURL(file);
                          const newPhoto = { file, preview, uploading: true, filename: null };
                          setPhotos(prev => [...prev, newPhoto]);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await axios.post(`${API}/upload-photo`, formData);
                            setPhotos(prev => prev.map((p, i) => i === prev.length - 1 ? { ...p, uploading: false, filename: res.data.filename } : p));
                          } catch {
                            toast({ title: "Erreur upload", description: "Impossible d'envoyer la photo", variant: "destructive" });
                            setPhotos(prev => prev.filter(p => p.preview !== preview));
                            URL.revokeObjectURL(preview);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Max {MAX_PHOTOS} photos (5 Mo chacune)</p>
              </div>

              {/* Info dimensions */}
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="dimensions-info">
                <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Pour permettre la livraison et la reprise de la benne, un espace dégagé minimum de <strong>4 m de hauteur</strong>, <strong>3,5 m de largeur</strong> et <strong>10 m de longueur</strong> doit être disponible.
                </p>
              </div>
            </Card>

            {/* Calendar & Time slot */}
            <Card className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-green-600" />
                Date et créneau de livraison
              </h2>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Date picker */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Date de livraison *</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="date-trigger"
                        className={`w-full h-11 justify-start text-left font-normal ${
                          !selectedDate ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        <CalendarDays className="mr-2 h-4 w-4 text-green-600" />
                        {selectedDate ? format(selectedDate, 'dd MMM yyyy', { locale: fr }) : "Choisir une date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                          setCalendarOpen(false);
                          // Auto-uncheck express supplement if date is not tomorrow
                          if (date && !isExpressAllowed(date)) {
                            setSelectedPrestation(previous => previous === 'express' ? 'depot' : previous);
                            setExpressSupplement(false);
                          }
                          if (expressSupplement && date) {
                            const tomorrowCheck = nextWorkingDeliveryDay();
                            if (date.toDateString() !== tomorrowCheck.toDateString()) {
                              setExpressSupplement(false);
                            }
                          }
                        }}
                        disabled={disabledDays}
                        locale={fr}
                        data-testid="calendar-picker"
                      />
                      <div className="px-4 pb-3 text-xs text-gray-500">
                        {isPast14h && !hasExpressAccess ? (
                          <span className="text-green-600 font-medium">
                            Après 16h : cochez le supplément Express (+100€ HT) pour livraison au prochain jour de livraison (hors week-end).
                          </span>
                        ) : (
                          'Week-ends et jours passés indisponibles. Minimum : prochain jour de livraison du lundi au vendredi.'
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {selectedDate && (
                    <p className="text-xs text-green-600 mt-1" data-testid="date-display">
                      {formatSelectedDate()}
                    </p>
                  )}

                  {/* Supplément Express - visible quand c'est après 14h et prestation n'est pas déjà express */}
                  {isPast14h && expressAvailableForDate && selectedPrestation !== 'express' && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="express-supplement-box">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={expressSupplement}
                          onChange={(e) => {
                            setExpressSupplement(e.target.checked);
                            if (!e.target.checked && selectedDate) {
                              const tomorrowCheck = nextWorkingDeliveryDay();
                              if (selectedDate.toDateString() === tomorrowCheck.toDateString()) {
                                setSelectedDate(undefined);
                              }
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-green-300 text-green-600 focus:ring-green-500"
                          data-testid="express-supplement-checkbox"
                        />
                        <div>
                          <span className="text-sm font-semibold text-green-700">Supplément Express (+100€ HT)</span>
                          <p className="text-xs text-green-600 mt-0.5">
                            Il est 16h passé. Cochez cette option pour débloquer le prochain jour de livraison (hors week-end).
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* Time slot */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Créneau horaire *</Label>
                  <div className="space-y-2" data-testid="time-slot-selector">
                    {TIME_SLOTS.map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setSelectedTimeSlot(prev => prev === value ? '' : value)}
                        data-testid={`time-slot-${value}`}
                        className={`w-full p-3 border-2 rounded-lg text-left text-sm transition-all flex items-center gap-2 ${
                          selectedTimeSlot === value
                            ? 'border-green-600 bg-green-50 ring-1 ring-green-200'
                            : 'border-gray-200 hover:border-green-300 bg-white'
                        }`}
                      >
                        <Clock className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="font-medium">{label}</span>
                      </button>
                    ))}
                    {selectedTimeSlot === 'custom' && (
                      <Input
                        data-testid="custom-time-input"
                        className="mt-2 h-11"
                        value={customTimeSlot}
                        onChange={(e) => setCustomTimeSlot(e.target.value)}
                        placeholder="Ex: 6h-10h, 14h-18h, 9h30..."
                      />
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right column: summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-6" data-testid="price-summary-card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Votre sélection</h3>

              <div className="space-y-2 mb-5 text-sm">
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Prestation</span>
                  <span className="font-semibold text-right">{PRESTATIONS.find(p => p.value === selectedPrestation)?.label || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Volume</span>
                  <span className="font-semibold">{selectedVolume || '—'}</span>
                </div>
                {selectedPrestation === 'echange' && selectedVolume && activeBenneVolumes.length > 0 && !activeBenneVolumes.includes(selectedVolume) && (
                  <div className="bg-amber-50 border border-amber-300 rounded-md p-2" data-testid="rotation-volume-warning">
                    <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      Attention : Volume précédent différent ({activeBenneVolumes.join(', ')})
                    </p>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Type</span>
                  <span className="font-semibold">{selectedType || '—'}</span>
                </div>
                {selectedPrestation === 'echange' && selectedType && activeBenneTypes.length > 0 && !activeBenneTypes.includes(selectedType) && (
                  <div className="bg-amber-50 border border-amber-300 rounded-md p-2" data-testid="rotation-type-warning">
                    <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      Attention : Type de déchet précédent différent ({activeBenneTypes.join(', ')})
                    </p>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Adresse</span>
                  <span className="font-semibold text-right max-w-[180px] truncate">
                    {showMap && latitude && longitude && !addressValid
                      ? `GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
                      : adresseChantier || '—'}
                  </span>
                </div>
                {showMap && latitude && longitude && addressValid && (
                  <div className="flex justify-between py-2 border-b border-dashed">
                    <span className="text-gray-500">GPS</span>
                    <span className="font-semibold text-xs">{latitude.toFixed(5)}, {longitude.toFixed(5)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Date</span>
                  <span className="font-semibold text-right text-xs">{selectedDate ? format(selectedDate, 'dd MMM yyyy', { locale: fr }) : '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Créneau</span>
                  <span className="font-semibold">{getTimeSlotValue() || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Accès</span>
                  <span className="font-semibold">{typeAcces === 'publique' ? 'Voie publique' : typeAcces === 'privee' ? 'Voie privée' : '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-dashed">
                  <span className="text-gray-500">Benne</span>
                  <span className="font-semibold">{typeBenne === 'chaine' ? 'À chaîne' : typeBenne === 'ampiroll' ? 'Ampiroll' : typeBenne === 'indifferent' ? 'Sans préférence' : '—'}</span>
                </div>
                {contactChantier && (
                  <div className="flex justify-between py-2 border-b border-dashed">
                    <span className="text-gray-500">Contact</span>
                    <span className="font-semibold text-right max-w-[180px] truncate">{contactChantier}</span>
                  </div>
                )}
                {telContactChantier && (
                  <div className="flex justify-between py-2 border-b border-dashed">
                    <span className="text-gray-500">Tél. contact</span>
                    <span className="font-semibold">{telContactChantier}</span>
                  </div>
                )}
                {commentaire && (
                  <div className="py-2 border-b border-dashed">
                    <span className="text-gray-500 text-xs">Commentaire</span>
                    <p className="font-semibold text-xs mt-0.5 break-words">{commentaire}</p>
                  </div>
                )}
                {adresseFactDifferente && adresseFacturation && (
                  <div className="py-2 border-b border-dashed" data-testid="summary-billing-address">
                    <span className="text-gray-500 text-xs">Adresse de facturation</span>
                    <p className="font-semibold text-xs mt-0.5 break-words">{adresseFacturation}{cpFacturation || villeFacturation ? `, ${cpFacturation} ${villeFacturation}`.trim() : ''}</p>
                  </div>
                )}
              </div>

              {calculatedPrice ? (
                <div className="bg-green-50 border-2 border-green-300 p-5 rounded-xl mb-4 text-center" data-testid="price-display">
                  <div className="text-xs text-gray-600 uppercase tracking-wider mb-1">Prix total HT</div>
                  {promoApplied ? (
                    <>
                      <div className="text-lg text-gray-400 line-through" data-testid="original-price">{calculatedPrice}€</div>
                      <div className="text-4xl font-extrabold text-green-600" data-testid="discounted-price">{promoApplied.discounted_price}€</div>
                      <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full mt-2" data-testid="promo-badge">
                        -{promoApplied.discount_display} avec {promoApplied.code}
                        <button onClick={() => { setPromoApplied(null); setPromoCode(''); }} className="ml-1 hover:text-green-900">&times;</button>
                      </div>
                    </>
                  ) : (
                    <div className="text-4xl font-extrabold text-green-600" data-testid="price-value">{calculatedPrice}€</div>
                  )}
                  <div className="text-sm font-semibold text-gray-700 mt-2">TVA (20 %) : {(Math.round((promoApplied?.discounted_price ?? calculatedPrice) * 20) / 100).toFixed(2)} € — Total TTC à payer : {(Math.round((promoApplied?.discounted_price ?? calculatedPrice) * 120) / 100).toFixed(2)} €</div>
                  <div className="text-xs text-gray-500 mt-2">Même tarif HT pour professionnels et particuliers. Forfait : 15 jours + transport + traitement.</div>
                </div>
              ) : (
                <div className="bg-gray-50 p-5 rounded-xl mb-4 text-center" data-testid="price-placeholder">
                  <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Sélectionnez une benne pour voir le prix</p>
                </div>
              )}

              {/* Code promo */}
              {calculatedPrice && !promoApplied && (
                <div className="mb-5" data-testid="promo-section">
                  <p className="text-sm font-bold text-gray-900 mb-2">Mon code promo</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                      placeholder="Code promo"
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="promo-input"
                      onKeyDown={(e) => e.key === 'Enter' && handleValidatePromo()}
                    />
                    <button
                      onClick={handleValidatePromo}
                      disabled={checkingPromo || !promoCode.trim()}
                      className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-bold px-5 py-2.5 rounded-lg text-sm transition-colors"
                      data-testid="promo-validate-btn"
                    >
                      {checkingPromo ? '...' : 'VALIDER'}
                    </button>
                  </div>
                  {promoError && (
                    <p className="text-red-500 text-xs mt-1.5" data-testid="promo-error">{promoError}</p>
                  )}
                </div>
              )}

              {/* Conditions d'évacuation */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-900 mb-2">Conditions d'évacuation</h4>

                <div className="space-y-2.5">
                  {[
                    { key: 'cond1', text: 'La mise à disposition de la benne comprend une durée d\'immobilisation de 15 jours calendaires sur votre chantier. Au-delà de cette période, la prolongation est facturée 100 € HT par tranche de 15 jours supplémentaires. Toute demande d’annulation ou de modification doit nous parvenir au moins 24 heures avant le début du créneau convenu ; contactez-nous au 07 45 55 88 35. Les droits légaux de rétractation restent applicables.' },
                    
                    { key: 'cond2', text: 'Le chargement de la benne ne doit en aucun cas dépasser la hauteur des ridelles.' },
                    { key: 'cond3', text: 'Il est strictement interdit de déposer des déchets dangereux ou réglementés, notamment : solvants, amiante, pneus, peintures, enrobés/goudrons sans analyse hydrocarbures aromatiques polycycliques, ainsi que tout autre déchet non autorisé.' },
                    { key: 'cond4', text: 'Pour permettre la livraison et la reprise de la benne dans de bonnes conditions, un espace dégagé minimum de 4 m de hauteur, 3,5 m de largeur et 10 m de longueur doit être disponible.' },
                    { key: 'cond5', text: 'Le client est responsable de la protection du sol à l\'endroit où la benne sera déposée. Il est recommandé d\'utiliser des protections adaptées (par ex des bastaings).' },
                    { key: 'cond6', text: 'Le client est tenu de garantir, au jour et à l\'heure convenus, l\'accessibilité du site ainsi que les conditions nécessaires à la réalisation de la prestation, et d\'être présent lors de l\'intervention. En cas d\'impossibilité d\'exécution imputable au client (absence, accès impossible, conditions non conformes), le déplacement sera considéré comme un passage à vide et fera l\'objet d\'une facturation de 180 € HT.' },
                  ].map(({ key, text }) => (
                    <label key={key} className="flex items-start gap-2.5 cursor-pointer group" data-testid={`condition-${key}`}>
                      <input
                        type="checkbox"
                        checked={!!acceptedConditions[key]}
                        onChange={(e) => setAcceptedConditions(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded-full border-2 border-green-400 text-green-500 focus:ring-green-400 accent-green-500 flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">{text}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Conditions générales */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Conditions générales</h4>
                <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="condition-cgu">
                  <input
                    type="checkbox"
                    checked={!!acceptedConditions.cgu}
                    onChange={(e) => setAcceptedConditions(prev => ({ ...prev, cgu: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded-full border-2 border-green-400 text-green-500 focus:ring-green-400 accent-green-500 flex-shrink-0"
                  />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    Je reconnais avoir pris connaissance et j'accepte les conditions générales d'utilisation (<a href="/cgu" target="_blank" className="text-blue-600 underline font-semibold">CGU</a>) et les conditions générales de vente (<a href="/cgv" target="_blank" className="text-blue-600 underline font-semibold">CGV</a>)
                  </span>
                </label>
              </div>

                <label className="flex items-center gap-1.5 cursor-pointer mb-3" data-testid="condition-check-all">
                    <input
                      type="checkbox"
                      checked={allConditionsAccepted}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setAcceptedConditions({ cond1: val, cond2: val, cond3: val, cond4: val, cond5: val, cond6: val, cgu: val });
                      }}
                      className="h-4 w-4 rounded border-2 border-green-500 text-green-600 focus:ring-green-400 accent-green-600"
                    />
                    <span className="text-xs font-medium text-green-700">Tout cocher</span>
                </label>

              {!isFormComplete && (selectedVolume || selectedType) && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4" data-testid="form-incomplete-warning">
                  <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {getMissingFieldMessage()}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={handleCommander}
                  disabled={!isFormComplete || isSubmitting}
                  data-testid="commander-button"
                  className={`w-full py-5 text-base font-bold ${
                    isFormComplete && !isSubmitting
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Traitement...</>
                  ) : (
                    <><ShoppingCart className="mr-2 h-5 w-5" />Commander</>
                  )}
                </Button>
                <Button
                  onClick={handleEnvoyerDevis}
                  disabled={!isDevisFormComplete || isSubmitting}
                  variant="outline"
                  data-testid="devis-email-button"
                  className={`w-full py-5 text-base border-2 ${
                    isDevisFormComplete && !isSubmitting
                      ? 'border-green-600 text-green-600 hover:bg-green-50'
                      : 'border-gray-300 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Recevoir le devis par email
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonCompte;
